import { useEffect, useState, type JSX } from 'react';
import { SharedRecipeResponse } from '@/types/api-models';
import { toaster } from '@/lib/toaster';
import Header from '@/components/Header';
import StarRating from '@/components/StarRating';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { RecipeClient } from '@/lib/recipeClient';
import { RecipeService } from '@/lib/recipeService';
import { getRecipeDisplayImageUrl } from '@/utils/imageUtils';
import { formatDuration } from '@/utils/formatDuration';
import { useAuth } from '@/hooks/useAuth';
import {
  Box, Button, Text, VStack, HStack, Heading, Image,
  Grid, GridItem, Skeleton, IconButton
} from '@chakra-ui/react';
import {
  MdRestaurantMenu as ServingsIcon, MdSchedule as TimeIcon, MdLocalFireDepartment as CookIcon,
  MdLock as LockIcon, MdAdd as AddIcon, MdArrowBack as BackIcon
} from 'react-icons/md';
import { RecipeIngredient } from '@/types/recipe';

export default function SharedRecipeView() {
  const [, navigate] = useLocation();
  const [match, params] = useRoute('/recipes/shared/:token');
  const token = params?.token || null;
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [mobileTab, setMobileTab] = useState<'ingredients' | 'instructions'>('ingredients');

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [token]);

  const { data: recipe, isLoading, error } = useQuery({
    queryKey: ['/recipes/shared', token],
    queryFn: async () => {
      if (!token) throw new Error('No share token');
      return RecipeClient.getSharedRecipe(token);
    },
    enabled: !!token,
    retry: false
  });

  const [isCopying, setIsCopying] = useState(false);

  const handleCopyRecipe = async () => {
    if (!isAuthenticated) { login(); return; }
    setIsCopying(true);
    try {
      if (!token) throw new Error('No share token');
      const recipe = await RecipeService.copyShared(token);
      toaster.create({ title: 'Saved to your collection!', type: 'success' });
      navigate(`/recipes/${recipe.id}`);
    } catch (err) {
      toaster.create({
        title: 'Copy failed',
        description: (err as Error).message || 'Unable to copy recipe. Please try again.',
        type: 'error',
      });
    } finally {
      setIsCopying(false);
    }
  };

  if (!match) return <NotFound navigate={navigate} />;
  if (isLoading || authLoading) return <Loading />;
  if (error || !recipe) return <NotFound navigate={navigate} />;

  const showPreviewOnly = !isAuthenticated;
  const isOwner = recipe.isOwner;
  const imageUrl = getRecipeDisplayImageUrl(recipe);

  const ingredients = showPreviewOnly ? (recipe.ingredients ?? []).slice(0, 5) : (recipe.ingredients ?? []);
  const instructions = showPreviewOnly ? (recipe.instructions ?? []).slice(0, 3) : (recipe.instructions ?? []);

  return (
    <Box minH="100vh" bg="bg.canvas">
      {/* Desktop header */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <Header title={recipe.title} />
      </Box>

      {/* ── Hero ── */}
      <Box position="relative" h={{ base: '260px', lg: '260px' }} overflow="hidden" flexShrink={0}>
        {imageUrl ? (
          <Image src={imageUrl} alt={recipe.title} w="100%" h="100%" objectFit="cover" />
        ) : (
          <Box
            w="100%" h="100%"
            style={{ background: 'linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)' }}
          />
        )}

        {/* Gradient overlay */}
        <Box
          position="absolute" inset={0}
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 30%, transparent 45%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.85) 100%)' }}
          pointerEvents="none"
        />

        {/* Back button — mobile only */}
        <Box
          position="absolute" top={0} left={0} right={0}
          px={{ base: 3, lg: 5 }}
          pt={{ base: 'calc(env(safe-area-inset-top, 0px) + 12px)', lg: 5 }}
          zIndex={10}
          display={{ base: 'block', lg: 'none' }}
        >
          <IconButton
            aria-label="Back"
            size="sm"
            borderRadius="full"
            color="white"
            style={{ background: 'rgba(0,0,0,0.38)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            _hover={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={() => navigate('/')}
          >
            <BackIcon size={20} />
          </IconButton>
        </Box>

        {/* Title + tags */}
        <Box position="absolute" bottom={0} left={0} right={0} p={{ base: 5, lg: 10 }}>
          <Heading
            fontFamily="heading"
            fontWeight="800"
            color="white"
            fontSize={{ base: '2xl', lg: '5xl' }}
            lineHeight="1.08"
            letterSpacing="-0.02em"
            mb={2}
          >
            {recipe.title}
          </Heading>

          {recipe.tags && recipe.tags.length > 0 && (
            <HStack gap={2} flexWrap="wrap">
              {recipe.tags.map((tag, i) => (
                <Box
                  key={i}
                  fontSize="11px"
                  fontWeight="500"
                  px={3}
                  py="3px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.35)"
                  color="white"
                  style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.32)' }}
                >
                  {tag}
                </Box>
              ))}
            </HStack>
          )}
        </Box>
      </Box>

      {/* ── Mobile: stat tiles ── */}
      <Box display={{ base: 'block', lg: 'none' }}>
        <Box bg="bg.surface" borderBottomWidth="1px" borderColor="border.muted" px={4} py={3}>
          <Grid templateColumns="1fr 1fr 1fr" gap={2}>
            <StatTile label="Servings" value={recipe.servings ?? undefined} icon={<ServingsIcon size={14} />} tint="teal" />
            <StatTile label="Prep" value={formatDuration(recipe.prepTime ?? undefined)} icon={<TimeIcon size={14} />} tint="teal" />
            <StatTile label="Cook" value={formatDuration(recipe.cookTime ?? undefined)} icon={<CookIcon size={14} />} tint="bronze" />
          </Grid>
        </Box>
      </Box>

      {/* ── Desktop: sidebar + two-col main ── */}
      <Box display={{ base: 'none', lg: 'grid' }} gridTemplateColumns="280px 1fr" minH="400px">
        {/* Sidebar */}
        <Box
          bg="bg.surface"
          borderRightWidth="1px"
          borderColor="border.muted"
          px={6}
          py={7}
          display="flex"
          flexDirection="column"
          gap={0}
          position="sticky"
          top={0}
          alignSelf="start"
        >
          <VStack align="stretch" gap={0}>
            <SidebarStatRow recipe={recipe} />

            <Box h="1px" bg="border.muted" my={5} />

            <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600" mb={2}>Rating</Text>
            <StarRating rating={recipe.rating || 0} onChange={() => {}} interactive={false} size={22} />

            {recipe.tags && recipe.tags.length > 0 && (
              <>
                <Box h="1px" bg="border.muted" my={5} />
                <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600" mb={2}>Tags</Text>
                <HStack gap={2} flexWrap="wrap">
                  {recipe.tags.map((tag, i) => (
                    <Box
                      key={i}
                      fontSize="11px"
                      fontWeight="500"
                      px="9px"
                      py="3px"
                      borderRadius="full"
                      bg="bg.muted"
                      color="text.muted"
                      border="1px solid"
                      borderColor="border.muted"
                    >
                      {tag}
                    </Box>
                  ))}
                </HStack>
              </>
            )}

            <Box h="1px" bg="border.muted" my={5} />

            {/* CTA */}
            {showPreviewOnly && (
              <Button
                bg="accent.emphasis"
                color="white"
                _hover={{ bg: 'accent.emphasisHover' }}
                fontFamily="heading"
                fontWeight="800"
                fontSize="sm"
                letterSpacing="0.06em"
                textTransform="uppercase"
                borderRadius="lg"
                h="42px"
                onClick={() => login()}
              >
                <LockIcon size={16} />
                Sign In to View
              </Button>
            )}
            {isAuthenticated && !isOwner && (
              <Button
                bg="accent.emphasis"
                color="white"
                _hover={{ bg: 'accent.emphasisHover' }}
                fontFamily="heading"
                fontWeight="800"
                fontSize="sm"
                letterSpacing="0.06em"
                textTransform="uppercase"
                borderRadius="lg"
                h="42px"
                loading={isCopying}
                onClick={handleCopyRecipe}
              >
                <AddIcon size={16} />
                Save to My Collection
              </Button>
            )}
            {isOwner && (
              <Button
                variant="ghost"
                color="accent.secondary"
                fontFamily="body"
                fontWeight="500"
                _hover={{ bg: 'accent.secondarySubtle', color: 'accent.secondaryHover' }}
                onClick={() => navigate(`/recipes/${recipe.id}`)}
              >
                View in your collection →
              </Button>
            )}
          </VStack>
        </Box>

        {/* Desktop main: ingredients + instructions side by side */}
        <Grid templateColumns="2fr 3fr" gap={12} px={10} py={8} bg="bg.canvas" alignItems="start">
          <GridItem>
            <SectionLabel>Ingredients</SectionLabel>
            <IngredientList ingredients={ingredients} />
            {showPreviewOnly && (recipe.ingredients ?? []).length > 5 && (
              <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted" mt={3} fontStyle="italic">
                + {(recipe.ingredients ?? []).length - 5} more ingredients — sign in to see all
              </Text>
            )}
          </GridItem>
          <GridItem>
            <SectionLabel>Instructions</SectionLabel>
            <InstructionList instructions={instructions} />
            {showPreviewOnly && (recipe.instructions ?? []).length > 3 && (
              <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted" mt={3} fontStyle="italic">
                + {(recipe.instructions ?? []).length - 3} more steps — sign in to see all
              </Text>
            )}
          </GridItem>
        </Grid>
      </Box>

      {/* ── Mobile: tabs + content ── */}
      <Box display={{ base: 'block', lg: 'none' }}>
        {/* Sticky tab bar */}
        <HStack
          gap={0}
          borderBottomWidth="1px"
          borderColor="border.muted"
          bg="bg.canvas"
          position="sticky"
          top="0"
          zIndex={50}
        >
          {(['ingredients', 'instructions'] as const).map((tab) => (
            <Box
              key={tab}
              as="button"
              flex={1}
              py={3}
              fontSize="13px"
              fontWeight={mobileTab === tab ? '600' : '500'}
              color={mobileTab === tab ? 'accent.emphasis' : 'text.muted'}
              borderBottomWidth="2px"
              borderColor={mobileTab === tab ? 'accent.emphasis' : 'transparent'}
              mb="-1px"
              bg="transparent"
              border="none"
              cursor="pointer"
              textTransform="capitalize"
              letterSpacing="0.02em"
              onClick={() => setMobileTab(tab)}
            >
              {tab}
            </Box>
          ))}
        </HStack>

        {/* Tab content */}
        <Box px={4} pt={5} pb={showPreviewOnly || (!isOwner && isAuthenticated) ? '100px' : '32px'}>
          {mobileTab === 'ingredients' && (
            <>
              <IngredientList ingredients={ingredients} />
              {showPreviewOnly && (recipe.ingredients ?? []).length > 5 && (
                <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted" mt={3} fontStyle="italic">
                  + {(recipe.ingredients ?? []).length - 5} more ingredients — sign in to see all
                </Text>
              )}
            </>
          )}
          {mobileTab === 'instructions' && (
            <>
              <InstructionList instructions={instructions} />
              {showPreviewOnly && (recipe.instructions ?? []).length > 3 && (
                <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted" mt={3} fontStyle="italic">
                  + {(recipe.instructions ?? []).length - 3} more steps — sign in to see all
                </Text>
              )}
            </>
          )}
        </Box>

        {/* Mobile sticky CTA bar */}
        {showPreviewOnly && (
          <Box
            position="fixed"
            bottom="14px"
            left={0} right={0}
            px={4} py={2}
            bg="bg.canvas"
            borderTopWidth="1px"
            borderColor="border.muted"
            zIndex={90}
          >
            <Button
              w="100%"
              bg="accent.emphasis"
              color="white"
              _hover={{ bg: 'accent.emphasisHover' }}
              fontFamily="heading"
              fontWeight="800"
              fontSize="sm"
              letterSpacing="0.06em"
              textTransform="uppercase"
              borderRadius="lg"
              h="42px"
              onClick={() => login()}
            >
              <LockIcon size={16} />
              Sign In to View Full Recipe
            </Button>
          </Box>
        )}
        {isAuthenticated && !isOwner && (
          <Box
            position="fixed"
            bottom="14px"
            left={0} right={0}
            px={4} py={2}
            bg="bg.canvas"
            borderTopWidth="1px"
            borderColor="border.muted"
            zIndex={90}
          >
            <Button
              w="100%"
              bg="accent.emphasis"
              color="white"
              _hover={{ bg: 'accent.emphasisHover' }}
              fontFamily="heading"
              fontWeight="800"
              fontSize="sm"
              letterSpacing="0.06em"
              textTransform="uppercase"
              borderRadius="lg"
              h="42px"
              loading={isCopying}
              onClick={handleCopyRecipe}
            >
              <AddIcon size={16} />
              Save to My Collection
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Sub-components ───────────────────────────────────────────

function NotFound({ navigate }: { navigate: (path: string) => void }) {
  return (
    <Box minH="100vh" bg="bg.canvas">
      <Header title="Recipe Not Found" showBackButton={true} onBackClick={() => navigate('/')} />
      <Box px={4} py={20} textAlign="center">
        <VStack gap={4}>
          <Box fontSize="6xl">🍽️</Box>
          <Heading fontFamily="heading" fontWeight="800" color="text.primary">Recipe not found</Heading>
          <Text color="text.muted" fontWeight="300">The shared recipe link is invalid or has been removed.</Text>
          <Button bg="accent.emphasis" color="white" _hover={{ bg: 'accent.emphasisHover' }} onClick={() => navigate('/')}>Go to Recipes</Button>
        </VStack>
      </Box>
    </Box>
  );
}

function Loading() {
  return (
    <Box minH="100vh" bg="bg.canvas">
      <Header title="Loading..." showBackButton={true} onBackClick={() => window.history.back()} />
      <Box px={4} py={20} textAlign="center">
        <VStack gap={4} align="center">
          <Skeleton height="32px" width="200px" />
          <Text fontSize="sm" color="text.muted" fontWeight="300">Loading recipe...</Text>
        </VStack>
      </Box>
    </Box>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      as="div"
      fontFamily="heading"
      fontWeight="800"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="text.muted"
      mb={3}
      pb={2}
      borderBottomWidth="2px"
      borderColor="accent.emphasis"
      display="inline-block"
    >
      {children}
    </Box>
  );
}

function SidebarStatRow({ recipe }: { recipe: SharedRecipeResponse }) {
  const stats = [
    { label: 'Servings', value: recipe.servings ?? null,                    icon: <ServingsIcon size={14} />, tint: 'teal' as const },
    { label: 'Prep',     value: formatDuration(recipe.prepTime ?? undefined) ?? null, icon: <TimeIcon size={14} />,     tint: 'teal' as const },
    { label: 'Cook',     value: formatDuration(recipe.cookTime ?? undefined) ?? null, icon: <CookIcon size={14} />,     tint: 'bronze' as const },
  ];

  return (
    <VStack align="stretch" gap={3}>
      {stats.map((s) => {
        const isTeal = s.tint === 'teal';
        const isEmpty = !s.value;
        return (
          <HStack key={s.label} gap={3} align="center" opacity={isEmpty ? 0.45 : 1}>
            <Box
              w="30px" h="30px" borderRadius="full"
              bg={isTeal ? 'accent.secondarySubtle' : '#fff3ec'}
              border="1px solid"
              borderColor={isTeal ? 'accent.secondaryBorder' : '#f5c5a8'}
              color={isTeal ? 'accent.secondary' : 'accent.emphasis'}
              display="flex" alignItems="center" justifyContent="center"
              fontSize="14px" flexShrink={0}
            >
              {s.icon}
            </Box>
            <VStack gap={0} align="start">
              <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600">{s.label}</Text>
              <Text fontSize="13px" fontWeight="600" color={isEmpty ? 'text.muted' : 'text.primary'}>
                {isEmpty ? '—' : String(s.value)}
              </Text>
            </VStack>
          </HStack>
        );
      })}
    </VStack>
  );
}

interface StatTileProps {
  label: string;
  value?: string | number | null;
  icon: JSX.Element;
  tint: 'teal' | 'bronze';
}

function StatTile({ label, value, icon, tint }: StatTileProps) {
  const isTeal = tint === 'teal';
  const isEmpty = !value;
  return (
    <VStack
      gap="2px"
      align="center"
      py={2}
      px={2}
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.muted"
      borderRadius="lg"
      opacity={isEmpty ? 0.45 : 1}
    >
      <Box color={isTeal ? 'accent.secondary' : 'accent.emphasis'} fontSize="16px" lineHeight="1">{icon}</Box>
      <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600">{label}</Text>
      <Text fontSize="13px" fontWeight="600" color={isEmpty ? 'text.muted' : 'text.primary'} lineHeight="1">
        {isEmpty ? '—' : String(value)}
      </Text>
    </VStack>
  );
}

function IngredientList({ ingredients }: { ingredients: RecipeIngredient[] }) {
  if (!ingredients || ingredients.length === 0) {
    return <Text fontSize="sm" color="text.muted" fontWeight="300">No ingredients listed.</Text>;
  }
  return (
    <VStack gap={0} align="stretch">
      {ingredients.map((ing, i) => (
        <HStack
          key={i}
          gap={3}
          py="9px"
          borderBottomWidth={i < ingredients.length - 1 ? '1px' : 0}
          borderColor="border.muted"
          align="baseline"
        >
          <Text fontSize="13px" fontWeight="600" color="accent.emphasis" flexShrink={0} whiteSpace="nowrap" w="70px">
            {ing.quantity}{ing.unit ? ` ${ing.unit}` : ''}
          </Text>
          <Text fontSize="14px" fontWeight="300" color="text.primary" minW="0">{ing.name}</Text>
        </HStack>
      ))}
    </VStack>
  );
}

function InstructionList({ instructions }: { instructions: string[] }) {
  if (!instructions || instructions.length === 0) {
    return <Text fontSize="sm" color="text.muted" fontWeight="300">No instructions provided.</Text>;
  }
  return (
    <VStack gap={4} align="stretch">
      {instructions.map((instruction, i) => (
        <HStack key={i} gap={3} align="flex-start">
          <Box
            w="26px" h="26px"
            borderRadius="full"
            bg="accent.secondary"
            color="white"
            fontSize="11px"
            fontWeight="600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            mt="2px"
          >
            {i + 1}
          </Box>
          <Text fontSize="14px" fontWeight="300" color="text.primary" lineHeight="tall" flex="1">
            {renderInstructionText(instruction, i)}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
}

function renderInstructionText(instruction: string, stepIndex: number): (string | JSX.Element)[] {
  const nodes: (string | JSX.Element)[] = [];
  const boldRegex = /\[(.+?)\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let partIndex = 0;
  while ((match = boldRegex.exec(instruction)) !== null) {
    if (match.index > lastIndex) nodes.push(instruction.slice(lastIndex, match.index));
    nodes.push(<Text as="span" key={`bold-${stepIndex}-${partIndex++}`} fontWeight="600">({match[1].trim()})</Text>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < instruction.length) nodes.push(instruction.slice(lastIndex));
  return nodes;
}
